/**
 * FramingsTab — section "Encadrements" de la page Réglages.
 *
 * CRUD complet sur le catalogue `framings`. `surcharge_cents` sert de
 * pré-remplissage indicatif quand Stéphanie crée une variante encadrée
 * (UX, pas vérité métier — le prix final reste sur `work_variants.price_cents`).
 */

import { useState } from 'react'
import { Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import KebabMenu from '@/components/ui/KebabMenu'
import Modal from '@/components/ui/Modal'
import { FormField } from '@/components/forms/FormField'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useCreateFraming,
  useDeleteFraming,
  useFramings,
  useUpdateFraming,
} from '@/hooks/useCatalog'
import {
  framingFormSchema,
  type FramingFormParsed,
  type FramingFormValues,
} from '@/schemas/catalog'
import type { FramingResponse } from '@/types/catalog'

function formatPriceEuros(cents: number): string {
  if (cents === 0) return '—'
  return `+${(cents / 100).toFixed(2)} €`
}

export default function FramingsTab() {
  const { confirm } = useConfirm()
  const framingsQuery = useFramings()
  const createMutation = useCreateFraming()
  const updateMutation = useUpdateFraming()
  const deleteMutation = useDeleteFraming()

  const [editing, setEditing] = useState<FramingResponse | null>(null)
  const [creating, setCreating] = useState(false)

  const handleToggleActive = (row: FramingResponse) => {
    updateMutation.mutate(
      { id: row.id, payload: { active: !row.active } },
      {
        onSuccess: () => {
          toast.success(row.active ? `« ${row.label_fr} » désactivé` : `« ${row.label_fr} » activé`)
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Mise à jour impossible')
        },
      },
    )
  }

  const handleDelete = async (row: FramingResponse) => {
    const ok = await confirm({
      title: 'Supprimer cet encadrement ?',
      message: `« ${row.label_fr} » sera retiré du catalogue. La suppression échoue si une variante l'utilise — désactive plutôt l'entrée.`,
      variant: 'danger',
      confirmText: 'Supprimer',
    })
    if (!ok) return
    deleteMutation.mutate(row.id, {
      onError: (err) => {
        toast.error(err.detail ?? 'Suppression impossible')
      },
      onSuccess: () => {
        toast.success(`Encadrement « ${row.label_fr} » supprimé`)
      },
    })
  }

  const rows = framingsQuery.data ?? []
  const isLoading = framingsQuery.isLoading

  return (
    <section className="settings-section">
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Encadrements</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Cadres proposés en option (couleur, matériau, surcharge prix indicative).
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" size={16} />
          Ajouter un encadrement
        </Button>
      </header>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title="Aucun encadrement dans ton catalogue"
          description="Ajoute « Sans encadrement », « Cadre blanc », « Cadre chêne »… selon ce que ton atelier propose."
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>
              Ajouter le premier encadrement
            </Button>
          }
        />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Libellé</th>
                <th>Couleur</th>
                <th>Matériau</th>
                <th>Surcharge</th>
                <th>Ordre</th>
                <th>Statut</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-inactive={!row.active || undefined}>
                  <td>
                    <strong>{row.label_fr}</strong>
                    {row.label_en ? (
                      <span className="settings-table-secondary">{row.label_en}</span>
                    ) : null}
                  </td>
                  <td>
                    {row.color_hex ? (
                      <span className="settings-color-swatch">
                        <span
                          className="settings-color-swatch-dot"
                          style={{ backgroundColor: row.color_hex }}
                          aria-hidden="true"
                        />
                        <code>{row.color_hex}</code>
                      </span>
                    ) : (
                      <span className="settings-table-secondary">—</span>
                    )}
                  </td>
                  <td>{row.material ?? '—'}</td>
                  <td className="settings-table-numeric">
                    {formatPriceEuros(row.surcharge_cents ?? 0)}
                  </td>
                  <td className="settings-table-numeric">{row.display_order}</td>
                  <td>
                    {row.active ? (
                      <Badge variant="success">Actif</Badge>
                    ) : (
                      <Badge variant="default">Désactivé</Badge>
                    )}
                  </td>
                  <td className="settings-table-actions">
                    <KebabMenu
                      ariaLabel={`Actions sur ${row.label_fr}`}
                      items={[
                        {
                          id: 'edit',
                          label: 'Modifier',
                          icon: Pencil,
                          onSelect: () => setEditing(row),
                        },
                        {
                          id: 'toggle',
                          label: row.active ? 'Désactiver' : 'Activer',
                          icon: row.active ? PowerOff : Power,
                          onSelect: () => handleToggleActive(row),
                        },
                        {
                          id: 'delete',
                          label: 'Supprimer',
                          icon: Trash2,
                          variant: 'danger',
                          onSelect: () => void handleDelete(row),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating ? (
        <FramingModal
          mode="create"
          onClose={() => setCreating(false)}
          onSubmit={(values) =>
            new Promise<void>((resolve, reject) => {
              createMutation.mutate(values, {
                onSuccess: () => {
                  toast.success(`Encadrement « ${values.label_fr} » ajouté`)
                  setCreating(false)
                  resolve()
                },
                onError: (err) => {
                  toast.error(err.detail ?? 'Création impossible')
                  reject(err)
                },
              })
            })
          }
        />
      ) : null}

      {editing ? (
        <FramingModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(values) =>
            new Promise<void>((resolve, reject) => {
              updateMutation.mutate(
                { id: editing.id, payload: values },
                {
                  onSuccess: () => {
                    toast.success(`Encadrement « ${values.label_fr} » mis à jour`)
                    setEditing(null)
                    resolve()
                  },
                  onError: (err) => {
                    toast.error(err.detail ?? 'Mise à jour impossible')
                    reject(err)
                  },
                },
              )
            })
          }
        />
      ) : null}
    </section>
  )
}

// ─── Modal create/edit ──────────────────────────────────────────────────

interface FramingModalProps {
  mode: 'create' | 'edit'
  initial?: FramingResponse
  onClose: () => void
  onSubmit: (values: FramingFormParsed) => Promise<void>
}

function FramingModal({ mode, initial, onClose, onSubmit }: FramingModalProps) {
  const form = useForm<FramingFormValues, unknown, FramingFormParsed>({
    resolver: zodResolver(framingFormSchema),
    defaultValues: {
      label_fr: initial?.label_fr ?? '',
      label_en: initial?.label_en ?? '',
      color_hex: initial?.color_hex ?? '',
      material: initial?.material ?? '',
      surcharge_cents: initial?.surcharge_cents ?? 0,
      display_order: initial?.display_order ?? 0,
    },
  })

  const submitting = form.formState.isSubmitting

  const handleValid = async (values: FramingFormParsed) => {
    await onSubmit(values)
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'Nouvel encadrement' : `Modifier ${initial?.label_fr}`}
      size="form"
      kicker="Catalogue · Encadrements"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleValid)} noValidate>
          <Modal.Body className="settings-form-grid">
            <FormField
              name="label_fr"
              label="Libellé"
              required
              placeholder="Cadre chêne"
              focusOnMount
            />
            <FormField name="label_en" label="Libellé EN (optionnel)" placeholder="Oak frame" />
            <div className="settings-form-row">
              <FormField
                name="color_hex"
                label="Couleur (#RRGGBB)"
                placeholder="Hex 6 chiffres"
                helper="Optionnel — affiche un swatch dans la table"
              />
              <FormField name="material" label="Matériau (optionnel)" placeholder="Chêne massif" />
            </div>
            <div className="settings-form-row">
              <FormField
                name="surcharge_cents"
                label="Surcharge (centimes)"
                type="number"
                min={0}
                helper="Indicatif — le prix final reste défini par variante"
              />
              <FormField name="display_order" label="Ordre d'affichage" type="number" min={0} />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" loading={submitting}>
              {mode === 'create' ? 'Ajouter' : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}
